import { getProfileContextGate } from "./profileContinuity";
import { buildProfileContextForAnalysisRest } from "./profileContinuityRest";

const VALID_4E_MODES = new Set(["ai", "jung", "freud"]);
const VALID_4E_TIERS = new Set(["fast", "deep"]);
const LANG_STORAGE_KEY = "morfeusz:lang";

const readCurrentLang = () => {
  if (typeof window === "undefined") return "pl";
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === "pl" || stored === "en") return stored;
  } catch (_) {
    /* ignore */
  }
  const docLang = String(document?.documentElement?.lang || "").toLowerCase();
  return docLang.startsWith("en") ? "en" : "pl";
};

const normalizeArray = (value) => {
  if (Array.isArray(value)) return value.filter(Boolean).map((item) => String(item).trim()).filter(Boolean);
  if (typeof value === "string" && value.trim()) {
    return value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);
  }
  return [];
};

const normalizeMode = (mode) => (VALID_4E_MODES.has(mode) ? mode : "ai");
const normalizeTier = (tier) => (VALID_4E_TIERS.has(tier) ? tier : "fast");

const normalizeDreamText = (dream) => {
  const text = dream?.bodyText || dream?.plainText || dream?.text || "";
  return String(text || "").replace(/\s+/g, " ").trim();
};

const normalizeDreamFor4E = (dream) => ({
  id: String(dream?.id || ""),
  title: String(dream?.title || "Sen bez tytułu").trim() || "Sen bez tytułu",
  bodyText: normalizeDreamText(dream),
  bodyHtml: String(dream?.bodyHtml || ""),
  date: String(dream?.date || ""),
  time: String(dream?.time || ""),
  emotions: normalizeArray(dream?.emotions),
  symbols: normalizeArray(dream?.symbols),
  saveMode: dream?.saveMode === "e2e" ? "e2e" : "plain",
});

const buildProfileSnapshotMeta = (profile) => ({
  hasTldr: Boolean(profile?.tldr),
  hasMotifs: Boolean(profile?.motifs),
  hasEmotions: Boolean(profile?.emotions),
  hasTimeline: Boolean(profile?.timeline),
  hasSamples: Boolean(profile?.samples),
});

export const build4EAnalyzePayload = async ({ dream, mode, tier = "fast", source = "morfeusz_front" } = {}) => {
  const normalizedMode = normalizeMode(mode);
  const normalizedTier = normalizeTier(tier);
  const normalizedDream = normalizeDreamFor4E(dream);
  const lang = readCurrentLang();
  const profileGate = getProfileContextGate();
  const profileContext = await buildProfileContextForAnalysisRest();
  const profileEnabled = Boolean(profileContext?.enabled);

  return {
    dreamId: normalizedDream.id,
    mode: normalizedMode,
    model: normalizedTier,
    tier: normalizedTier,
    lang,
    dream: normalizedDream,
    profile: profileEnabled
      ? {
          enabled: true,
          tldr: profileContext.tldr || "",
          motifs: profileContext.motifs || null,
          emotions: profileContext.emotions || null,
          timeline: profileContext.timeline || null,
          samples: profileContext.samples || null,
        }
      : {
          enabled: false,
          reason: profileContext?.reason || profileGate?.reason || "disabled",
        },
    profileGate: {
      enabled: Boolean(profileEnabled || profileGate?.enabled),
      reason: profileContext?.reason || profileGate?.reason || "unknown",
      buildOptin: Boolean(profileContext?.buildOptin || profileGate?.buildOptin),
      aiUseOptin: Boolean(profileContext?.aiUseOptin || profileGate?.aiUseOptin),
      hasPack: Boolean(profileContext?.hasPack || profileGate?.hasPack),
      version: profileGate?.version || null,
      updatedAt: profileContext?.updatedAt || profileGate?.updatedAt || "",
      lastRebuiltAt: profileContext?.lastRebuiltAt || profileGate?.lastRebuiltAt || "",
    },
    profileSnapshotMeta: profileEnabled ? buildProfileSnapshotMeta(profileContext) : null,
    client: {
      source,
      appVersion: "front-wp-rest-v0.2.1",
      lang,
    },
  };
};

export const assert4EAnalyzePayload = (payload) => {
  if (!payload || typeof payload !== "object") {
    return { ok: false, reason: "payload_missing" };
  }

  if (!payload.dreamId) {
    return { ok: false, reason: "dream_id_missing" };
  }

  if (!VALID_4E_MODES.has(payload.mode)) {
    return { ok: false, reason: "invalid_mode" };
  }

  if (!VALID_4E_TIERS.has(payload.model || payload.tier || "fast")) {
    return { ok: false, reason: "invalid_model" };
  }

  if (!payload.dream?.bodyText) {
    return { ok: false, reason: "dream_text_missing" };
  }

  return { ok: true, reason: "ok" };
};
